import React, { useState } from "react";
import { Plus, Clock, X, GripVertical, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { SubjectCombobox, type SubjectOption } from "../AttendanceManagement/SubjectCombobox";
import { TeacherCombobox, type TeacherOption } from "./TeacherCombobox";

// ── Types ─────────────────────────────────────────────────────────────────────
// subjectId is a live reference into the Subject Registry — the backend
// (ClassRoomModule.getTimetable) re-resolves it to fresh subject text on
// every read, so renaming a subject updates every timetable using it
// automatically. `subject` is always kept as the last-known display text
// too (used as-is for legacy slots with no subjectId, and as a fallback if
// the subject is ever deleted) — it's never actually blank/stale-looking.
//
// subjectTeacher is entirely optional — a slot is valid with a subject and
// no teacher, same as it's valid with no subject today. When present, the
// backend (ClassRoom.module.js#_resolveTimetableTeachers) refreshes `name`
// from the assigned faculty's live Auth record on every read, same
// "resolve fresh from source" treatment subjectId already gets.
export type SubjectTeacher = {
    uid: string;
    name: string;
    eid?: string;
    email?: string;
};

export type TimetableSlot = {
    subject: string;
    subjectId?: string | null;
    room: string;
    subjectTeacher?: SubjectTeacher | null;
};

export type TimetableGrid = {
    periods: string[];
    days: string[];
    grid: Record<string, (TimetableSlot | null)[]>;
};

const ALL_DAYS = [ 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday' ];
const DEFAULT_DAYS = [ 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday' ];

export function createEmptyGrid(): TimetableGrid {
    const periods = [ '09:00-10:00', '10:00-11:00', '11:00-12:00', '12:00-01:00' ];
    const days = [ ...DEFAULT_DAYS ];
    const grid: Record<string, (TimetableSlot | null)[]> = {};
    for (const day of days) {
        grid[ day ] = periods.map(() => null);
    }
    return { periods, days, grid };
}

// ── Cell Editor ───────────────────────────────────────────────────────────────
type CellEditorProps = {
    value: TimetableSlot | null;
    onChange: (v: TimetableSlot | null) => void;
};

// Dragged palette chips carry one of these MIME types + a JSON payload —
// checked so a cell doesn't react to random drags of unrelated page
// content. Subject and teacher are dropped independently of one another:
// dropping a teacher chip onto a cell that already has a subject keeps the
// subject (and vice versa), since a subjectTeacher is optional and unrelated
// to whether a subject is already scheduled there.
const SUBJECT_DRAG_MIME = "application/x-subject-chip";
const TEACHER_DRAG_MIME = "application/x-teacher-chip";

function CellEditor({ value, onChange }: CellEditorProps) {
    const [ editing, setEditing ] = useState(false);
    const [ draft, setDraft ] = useState<TimetableSlot>(value || { subject: '', room: '' });
    const [ dragOver, setDragOver ] = useState(false);

    const handleDragOver = (e: React.DragEvent) => {
        if (!e.dataTransfer.types.includes(SUBJECT_DRAG_MIME) && !e.dataTransfer.types.includes(TEACHER_DRAG_MIME)) return;
        e.preventDefault();
        setDragOver(true);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);

        const subjectRaw = e.dataTransfer.getData(SUBJECT_DRAG_MIME);
        if (subjectRaw) {
            try {
                const dropped = JSON.parse(subjectRaw) as { id: string; label: string };
                onChange({
                    subject: dropped.label,
                    subjectId: dropped.id,
                    room: value?.room || '',
                    subjectTeacher: value?.subjectTeacher ?? null,
                });
            } catch { /* malformed drag payload — ignore */ }
            return;
        }

        const teacherRaw = e.dataTransfer.getData(TEACHER_DRAG_MIME);
        if (teacherRaw) {
            try {
                const dropped = JSON.parse(teacherRaw) as SubjectTeacher;
                onChange({
                    subject: value?.subject || '',
                    subjectId: value?.subjectId ?? null,
                    room: value?.room || '',
                    subjectTeacher: dropped,
                });
            } catch { /* malformed drag payload — ignore */ }
        }
    };

    const removeTeacher = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!value) return;
        onChange({ ...value, subjectTeacher: null });
    };

    if (!editing) {
        return value ? (
            <div
                onClick={() => { setDraft(value); setEditing(true); }}
                onDragOver={handleDragOver}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={`group relative h-full min-h-[72px] w-full cursor-pointer rounded-lg bg-primary/10 border p-2.5 hover:bg-primary/15 transition-all ${dragOver ? "border-primary border-2" : "border-primary/20"}`}
            >
                <p className="text-[12px] font-black text-primary leading-tight">{value.subject}</p>
                {value.room && (
                    <p className="text-[10px] text-muted-foreground mt-1">{value.room}</p>
                )}
                {value.subjectTeacher && (
                    <div className="flex items-center gap-1 mt-1.5 group/teacher">
                        <UserRound className="h-2.5 w-2.5 text-emerald-600 shrink-0" />
                        <p className="text-[10px] font-bold text-emerald-700 truncate">{value.subjectTeacher.name}</p>
                        <button
                            onClick={removeTeacher}
                            className="opacity-0 group-hover/teacher:opacity-100 transition-opacity h-3 w-3 rounded-full bg-red-100 flex items-center justify-center shrink-0"
                            title="Remove teacher"
                        >
                            <X className="h-2 w-2 text-red-500" />
                        </button>
                    </div>
                )}
                <button
                    onClick={(e) => { e.stopPropagation(); onChange(null); }}
                    className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity h-4 w-4 rounded-full bg-red-100 flex items-center justify-center"
                    title="Remove period"
                >
                    <X className="h-2.5 w-2.5 text-red-500" />
                </button>
            </div>
        ) : (
            <div
                onClick={() => { setDraft({ subject: '', room: '' }); setEditing(true); }}
                onDragOver={handleDragOver}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                className={`h-full min-h-[72px] w-full cursor-pointer rounded-lg border border-dashed flex items-center justify-center hover:border-primary/40 hover:bg-primary/5 transition-all group ${dragOver ? "border-primary border-2 bg-primary/5" : "border-border/40"}`}
            >
                <Plus className="h-4 w-4 text-muted-foreground/30 group-hover:text-primary/50 transition-colors" />
            </div>
        );
    }

    return (
        <div className="h-full min-h-[72px] w-full rounded-lg border border-primary bg-card p-2 space-y-1.5 shadow-md">
            <Input
                autoFocus
                placeholder="Subject"
                value={draft.subject}
                onChange={(e) => setDraft(d => ({ ...d, subject: e.target.value }))}
                className="h-7 text-[11px] px-2 border-border/40"
            />
            <Input
                placeholder="Room (optional)"
                value={draft.room}
                onChange={(e) => setDraft(d => ({ ...d, room: e.target.value }))}
                className="h-7 text-[11px] px-2 border-border/40"
            />
            <TeacherCombobox
                value={draft.subjectTeacher?.name || ''}
                placeholder="Subject teacher (optional)"
                onSelect={(t) => setDraft(d => ({
                    ...d,
                    subjectTeacher: { uid: t.uid, name: t.name, eid: t.employee_id, email: t.email },
                }))}
            />
            <div className="flex gap-1">
                <Button
                    size="sm"
                    className="h-6 text-[10px] flex-1 px-1"
                    onClick={() => {
                        if (draft.subject.trim()) {
                            onChange(draft);
                            setEditing(false);
                        }
                    }}
                >
                    OK
                </Button>
                <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 text-[10px] px-2"
                    onClick={() => setEditing(false)}
                >
                    ✕
                </Button>
            </div>
        </div>
    );
}

// ── Period Editor ─────────────────────────────────────────────────────────────
type PeriodEditorProps = {
    value: string;
    onChange: (v: string) => void;
};

function PeriodEditor({ value, onChange }: PeriodEditorProps) {
    const [ editing, setEditing ] = useState(false);
    const [ draft, setDraft ] = useState(value);

    if (!editing) {
        return (
            <div
                onClick={() => { setDraft(value); setEditing(true); }}
                className="flex items-center gap-1.5 cursor-pointer group min-w-0"
            >
                <Clock className="h-3 w-3 text-muted-foreground/50 shrink-0" />
                <span className="text-[11px] font-black uppercase tracking-wide text-muted-foreground group-hover:text-primary transition-colors truncate">
                    {value}
                </span>
            </div>
        );
    }

    return (
        <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => { onChange(draft); setEditing(false); }}
            onKeyDown={(e) => {
                if (e.key === 'Enter') { onChange(draft); setEditing(false); }
                if (e.key === 'Escape') setEditing(false);
            }}
            className="w-full text-[11px] font-black uppercase border border-primary rounded px-1.5 py-0.5 outline-none bg-card"
            placeholder="09:00-10:00"
        />
    );
}

// ── Subject Palette Chip — draggable onto any grid cell ────────────────────────
type PaletteSubject = { id: string; code: string; label: string };

function SubjectPaletteChip({ subject, onRemove }: { subject: PaletteSubject; onRemove: () => void }) {
    return (
        <div
            draggable
            onDragStart={(e) => {
                e.dataTransfer.setData(SUBJECT_DRAG_MIME, JSON.stringify({ id: subject.id, label: subject.label }));
                e.dataTransfer.effectAllowed = "copy";
            }}
            className="group flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full border border-primary/30 bg-primary/10 cursor-grab active:cursor-grabbing select-none"
            title="Drag onto a period to schedule it"
        >
            <GripVertical className="h-3 w-3 text-primary/50 shrink-0" />
            <span className="text-[11px] font-bold text-primary whitespace-nowrap">{subject.label}</span>
            <button
                onClick={onRemove}
                className="h-4 w-4 rounded-full hover:bg-red-100 flex items-center justify-center shrink-0"
            >
                <X className="h-2.5 w-2.5 text-red-400" />
            </button>
        </div>
    );
}

// ── Teacher Palette Chip — draggable onto any grid cell, independent of subject ─
function TeacherPaletteChip({ teacher, onRemove }: { teacher: SubjectTeacher; onRemove: () => void }) {
    return (
        <div
            draggable
            onDragStart={(e) => {
                e.dataTransfer.setData(TEACHER_DRAG_MIME, JSON.stringify(teacher));
                e.dataTransfer.effectAllowed = "copy";
            }}
            className="group flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 cursor-grab active:cursor-grabbing select-none"
            title="Drag onto a period to assign as subject teacher"
        >
            <GripVertical className="h-3 w-3 text-emerald-600/50 shrink-0" />
            <UserRound className="h-3 w-3 text-emerald-600/70 shrink-0" />
            <span className="text-[11px] font-bold text-emerald-700 whitespace-nowrap">{teacher.name}</span>
            <button
                onClick={onRemove}
                className="h-4 w-4 rounded-full hover:bg-red-100 flex items-center justify-center shrink-0"
            >
                <X className="h-2.5 w-2.5 text-red-400" />
            </button>
        </div>
    );
}

// ── Main Timetable Editor ─────────────────────────────────────────────────────
type Props = {
    open: boolean;
    onClose: () => void;
    onSave: (grid: TimetableGrid) => Promise<void>;
    isSaving: boolean;
    classroomName: string;
    initial?: TimetableGrid | null;
};

export function TimetableEditor({
    open,
    onClose,
    onSave,
    isSaving,
    classroomName,
    initial,
}: Props) {
    const [ grid, setGrid ] = useState<TimetableGrid>(() => initial || createEmptyGrid());

    // Subjects picked from the registry for THIS editing session — search,
    // add to the palette, then drag any chip onto a period cell. Cleared
    // each time the dialog reopens; it's a working set, not saved data.
    const [ paletteSubjects, setPaletteSubjects ] = useState<PaletteSubject[]>([]);

    // Same working-set pattern for subject teachers — entirely optional,
    // independent of the subject palette above.
    const [ paletteTeachers, setPaletteTeachers ] = useState<SubjectTeacher[]>([]);

    React.useEffect(() => {
        if (open) {
            setGrid(initial || createEmptyGrid());
            setPaletteSubjects([]);
            setPaletteTeachers([]);
        }
    }, [ open, initial ]);

    const addToPalette = (s: SubjectOption) => {
        const label = s.subject_name ? `${s.subject_name} (${s.subject_code})` : s.subject_code;
        setPaletteSubjects(prev => prev.some(p => p.code === s.subject_code) ? prev : [ ...prev, { id: s.id, code: s.subject_code, label } ]);
    };

    const removeFromPalette = (code: string) => {
        setPaletteSubjects(prev => prev.filter(p => p.code !== code));
    };

    const addTeacherToPalette = (t: TeacherOption) => {
        setPaletteTeachers(prev => prev.some(p => p.uid === t.uid)
            ? prev
            : [ ...prev, { uid: t.uid, name: t.name, eid: t.employee_id, email: t.email } ]);
    };

    const removeTeacherFromPalette = (uid: string) => {
        setPaletteTeachers(prev => prev.filter(p => p.uid !== uid));
    };

    // ── Period management ─────────────────────────────────────────────────────
    const addPeriod = () => {
        setGrid(g => {
            const newPeriod = `Period ${g.periods.length + 1}`;
            const newGrid = { ...g.grid };
            for (const day of g.days) {
                newGrid[ day ] = [ ...newGrid[ day ], null ];
            }
            return { ...g, periods: [ ...g.periods, newPeriod ], grid: newGrid };
        });
    };

    const removePeriod = (idx: number) => {
        setGrid(g => {
            const newPeriods = g.periods.filter((_, i) => i !== idx);
            const newGrid = { ...g.grid };
            for (const day of g.days) {
                newGrid[ day ] = newGrid[ day ].filter((_, i) => i !== idx);
            }
            return { ...g, periods: newPeriods, grid: newGrid };
        });
    };

    const updatePeriod = (idx: number, value: string) => {
        setGrid(g => {
            const newPeriods = [ ...g.periods ];
            newPeriods[ idx ] = value;
            return { ...g, periods: newPeriods };
        });
    };

    // ── Day management ────────────────────────────────────────────────────────
    const addDay = (day: string) => {
        setGrid(g => {
            if (g.days.includes(day)) return g;
            const newGrid = { ...g.grid, [ day ]: g.periods.map(() => null) };
            const newDays = ALL_DAYS.filter(d => g.days.includes(d) || d === day);
            return { ...g, days: newDays, grid: newGrid };
        });
    };

    const removeDay = (day: string) => {
        setGrid(g => {
            const newDays = g.days.filter(d => d !== day);
            const newGrid = { ...g.grid };
            delete newGrid[ day ];
            return { ...g, days: newDays, grid: newGrid };
        });
    };

    // ── Cell update ───────────────────────────────────────────────────────────
    const updateCell = (day: string, periodIdx: number, value: TimetableSlot | null) => {
        setGrid(g => {
            const newDayRow = [ ...g.grid[ day ] ];
            newDayRow[ periodIdx ] = value;
            return { ...g, grid: { ...g.grid, [ day ]: newDayRow } };
        });
    };

    const availableDays = ALL_DAYS.filter(d => !grid.days.includes(d));
    const scheduledSlots = Object.values(grid.grid).flat().filter(Boolean) as TimetableSlot[];
    const totalScheduled = scheduledSlots.length;
    const totalWithTeacher = scheduledSlots.filter(s => s.subjectTeacher?.uid).length;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent
                className="border-border/60 p-0 overflow-hidden shadow-2xl"
                style={{
                    width: '98vw',
                    maxWidth: '98vw',
                    height: '92vh',
                    maxHeight: '92vh',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Header */}
                <DialogHeader className="p-5 pr-14 border-b bg-muted/5 shrink-0">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <DialogTitle className="text-xl font-bold">
                                Timetable — {classroomName}
                            </DialogTitle>
                            <p className="text-[10px] text-muted-foreground uppercase font-black tracking-widest mt-0.5">
                                Search a subject or teacher below, then drag onto a period (teacher optional) · Click period label to rename · Hover row to remove day
                            </p>
                        </div>

                        {/* Controls */}
                        <div className="flex items-center gap-2 flex-wrap justify-end shrink-0">
                            {availableDays.length > 0 && (
                                <div className="flex items-center gap-1 flex-wrap">
                                    <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mr-1">
                                        Add day:
                                    </span>
                                    {availableDays.map(day => (
                                        <Badge
                                            key={day}
                                            variant="outline"
                                            className="cursor-pointer hover:bg-primary hover:text-white transition-all text-[10px] font-black px-2 py-0.5"
                                            onClick={() => addDay(day)}
                                        >
                                            + {day.substring(0, 3)}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-[11px] font-bold gap-1.5"
                                onClick={addPeriod}
                            >
                                <Plus className="h-3.5 w-3.5" /> Add Period
                            </Button>
                        </div>
                    </div>
                </DialogHeader>

                {/* Subject palette — search/add here, then drag any chip onto a cell */}
                <div className="px-5 py-3 border-b bg-muted/5 shrink-0 flex items-center gap-3 flex-wrap">
                    <div className="w-64 shrink-0">
                        <SubjectCombobox value="" placeholder="Search & add a subject…" onSelect={addToPalette} />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {paletteSubjects.length === 0 ? (
                            <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">
                                No subjects added yet
                            </span>
                        ) : (
                            paletteSubjects.map(s => (
                                <SubjectPaletteChip key={s.code} subject={s} onRemove={() => removeFromPalette(s.code)} />
                            ))
                        )}
                    </div>
                </div>

                {/* Teacher palette — optional, search/add here, then drag any chip
                    onto a cell to assign a subject teacher; independent of subject */}
                <div className="px-5 py-3 border-b bg-muted/5 shrink-0 flex items-center gap-3 flex-wrap">
                    <div className="w-64 shrink-0">
                        <TeacherCombobox value="" placeholder="Search & add a teacher (optional)…" onSelect={addTeacherToPalette} />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {paletteTeachers.length === 0 ? (
                            <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">
                                No teachers added yet
                            </span>
                        ) : (
                            paletteTeachers.map(t => (
                                <TeacherPaletteChip key={t.uid} teacher={t} onRemove={() => removeTeacherFromPalette(t.uid)} />
                            ))
                        )}
                    </div>
                </div>

                {/* Grid — takes all remaining space */}
                <div className="flex-1 overflow-auto p-4 bg-[oklch(0.98_0.005_90)]">
                    <table
                        className="border-collapse"
                        style={{
                            width: '100%',
                            minWidth: `${160 + grid.periods.length * 180}px`,
                        }}
                    >
                        <thead>
                            <tr>
                                {/* Corner */}
                                <th
                                    className="p-3 text-left bg-card border border-border/30 rounded-tl-lg"
                                    style={{ width: '130px', minWidth: '130px' }}
                                >
                                    <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60">
                                        Day / Period
                                    </span>
                                </th>

                                {grid.periods.map((period, idx) => (
                                    <th
                                        key={idx}
                                        className="p-3 bg-card border border-border/30"
                                        style={{ minWidth: '180px' }}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <PeriodEditor
                                                value={period}
                                                onChange={(v) => updatePeriod(idx, v)}
                                            />
                                            <button
                                                onClick={() => removePeriod(idx)}
                                                className="h-5 w-5 rounded-full bg-red-50 flex items-center justify-center hover:bg-red-100 transition-colors shrink-0 ml-1"
                                            >
                                                <X className="h-3 w-3 text-red-400" />
                                            </button>
                                        </div>
                                    </th>
                                ))}

                                {/* Empty add column hint */}
                                <th className="p-3 bg-transparent" style={{ width: '60px' }}>
                                    <button
                                        onClick={addPeriod}
                                        className="h-7 w-7 rounded-full border border-dashed border-border/40 flex items-center justify-center hover:border-primary/40 hover:bg-primary/5 transition-all mx-auto"
                                    >
                                        <Plus className="h-3.5 w-3.5 text-muted-foreground/40" />
                                    </button>
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {grid.days.map((day, dayIdx) => (
                                <tr key={day} className="group/row">
                                    {/* Day label */}
                                    <td
                                        className="p-3 bg-card border border-border/30 align-middle"
                                        style={{ width: '130px', minWidth: '130px' }}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-[13px] font-black text-foreground">{day}</p>
                                                <p className="text-[9px] text-muted-foreground mt-0.5">
                                                    {grid.grid[ day ]?.filter(Boolean).length || 0} classes
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => removeDay(day)}
                                                className="opacity-0 group-hover/row:opacity-100 transition-opacity h-6 w-6 rounded-full bg-red-50 flex items-center justify-center hover:bg-red-100 shrink-0"
                                            >
                                                <X className="h-3 w-3 text-red-400" />
                                            </button>
                                        </div>
                                    </td>

                                    {/* Cells */}
                                    {grid.periods.map((_, pIdx) => (
                                        <td
                                            key={pIdx}
                                            className="p-2 border border-border/20 align-top bg-background/50"
                                        >
                                            <CellEditor
                                                value={grid.grid[ day ]?.[ pIdx ] ?? null}
                                                onChange={(v) => updateCell(day, pIdx, v)}
                                            />
                                        </td>
                                    ))}

                                    {/* Empty trailing cell */}
                                    <td className="border border-transparent" />
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <DialogFooter className="p-4 border-t bg-muted/5 shrink-0">
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1.5">
                                <div className="h-2 w-2 rounded-full bg-primary" />
                                <span className="text-[11px] font-bold text-muted-foreground">
                                    {grid.days.length} days
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="h-2 w-2 rounded-full bg-blue-400" />
                                <span className="text-[11px] font-bold text-muted-foreground">
                                    {grid.periods.length} periods
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="h-2 w-2 rounded-full bg-emerald-400" />
                                <span className="text-[11px] font-bold text-muted-foreground">
                                    {totalScheduled} classes scheduled
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="h-2 w-2 rounded-full bg-emerald-600" />
                                <span className="text-[11px] font-bold text-muted-foreground">
                                    {totalWithTeacher} with a teacher assigned
                                </span>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <DialogClose asChild>
                                <Button
                                    variant="ghost"
                                    className="text-xs font-bold uppercase"
                                    onClick={onClose}
                                >
                                    Cancel
                                </Button>
                            </DialogClose>
                            <Button
                                onClick={() => onSave(grid)}
                                disabled={isSaving}
                                className="min-w-[160px] text-xs font-bold uppercase shadow-lg shadow-primary/20"
                            >
                                {isSaving
                                    ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving...</>
                                    : 'Save Timetable'
                                }
                            </Button>
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}